import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useGetUserByEmailQuery } from '../../redux/thunks/users/getuserbyemail';
import { setSignIn } from '../../redux/slices/auth';
import { useGetActivitiesQuery } from '../../redux/thunks/users/getactivities';
import { useGetNetworkStatsQuery } from '../../redux/thunks/users/getnetworkstats';
import { useGetUserTrendingQuery } from '../../redux/thunks/users/gettrending';
import { useGetBusinessStatsQuery } from '../../redux/thunks/sp/getbusinessstats';
import {
  useGetDraftsQuery,
  useApproveDraftMutation,
  useRejectDraftMutation
} from '../../redux/thunks/drafts/getdrafts';
import BasicSEO from '../../Components/SEO/BasicSEO';
import moment from 'moment';

import { usePrefetch as useGetStatsGlobalPrefetch }      from '../../redux/thunks/stats/statsglobal';
import { usePrefetch as useGetUserChatListPrefetch }      from '../../redux/thunks/users/userchatlist';
import { usePrefetch as useGetUserNotificationsPrefetch } from '../../redux/thunks/users/usernotifications';
import { useGetUserProfileQuery } from '../../redux/thunks/users/userprofile';
import { useSetAffiliateAttributionMutation } from '../../redux/thunks/sp/affiliate';
import { takePendingDestination, clearPendingDestination } from '../../Functions/PendingDestination';


import {
  Box, Container, Grid, Paper, Typography, Avatar, Button, Chip,
  LinearProgress, IconButton, Skeleton, Card, CardContent, Rating,
  Alert, Snackbar, CardActions, Collapse, useTheme, useMediaQuery,
  Tabs, Tab, Stack, Badge, Divider, Tooltip
} from '@mui/material';

import {
  SearchOutlined as SearchIcon,
  AddBusinessOutlined as AddBusinessIcon,
  RateReviewOutlined as ReviewIcon,
  TrendingUpOutlined as TrendingIcon,
  PeopleOutline as PeopleIcon,
  BusinessOutlined as BusinessIcon,
  ErrorOutline as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  AutoGraph as AutoGraphIcon,
  TrendingUp as TrendingUpIcon,
  LocalActivity as LocalActivityIcon,
  RocketLaunch as RocketLaunchIcon,
  StorefrontOutlined as StoreIcon,
  Star as StarIcon,
  HowToReg as HowToRegIcon,
  FollowTheSigns as FollowTheSignsIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  GroupAdd as GroupAddIcon,
  BusinessCenter as BusinessCenterIcon,
  Lightbulb as LightbulbIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as AIIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as RejectIcon,
  Pending as PendingIcon,
  RateReview as RateReviewIcon,
  QuestionAnswer as FAQIcon,
  Campaign as SocialIcon,
  Article as ArticleIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// ===== Existing constants — unchanged =====

const BusinessInsightChip = ({ type, value }) => {
  const getChipProps = () => {
    switch (type) {
      case 'network':  return { icon: <PeopleIcon />,      color: 'primary'  };
      case 'ai':       return { icon: <AutoGraphIcon />,   color: 'secondary' };
      case 'trending': return { icon: <TrendingUpIcon />,  color: 'success'  };
      case 'popular':  return { icon: <TrendingUpIcon />,  color: 'success'  };
      case 'new':      return { icon: <RocketLaunchIcon />, color: 'success' };
      case 'event':    return { icon: <LocalActivityIcon />, color: 'success' };
      default:         return { color: 'default' };
    }
  };
  return <Chip size="small" label={value} variant="outlined" {...getChipProps()} />;
};

// ===== ActivityOverview — unchanged =====
const ActivityOverview = ({ userData, isLoading }) => {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(!isMobile);

  useEffect(() => { setIsExpanded(!isMobile); }, [isMobile]);

  const getInitials = (name) => {
    if (!name) return '';
    const [firstName, lastName] = name.split(' ');
    return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  };

  if (isLoading || !userData) {
    return (
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="circular" width={60} height={60} />
            <Box sx={{ ml: 2 }}>
              <Skeleton variant="text" width={120} />
              <Skeleton variant="text" width={80} />
            </Box>
          </Box>
          <Skeleton variant="text" width="60%" />
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
          <Skeleton variant="text" width={150} sx={{ mb: 2 }} />
          {[1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="rectangular" height={70} />
            </Box>
          ))}
        </Paper>
      </Paper>
    );
  }





  const formattedDate = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'long', day: '2-digit'
  }).format(new Date(userData.creationTime));

  return (
    <Box sx={{ mb: 3 }}>
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2, border: 'none', cursor: 'pointer' }}
        onClick={() => navigate(`/profile/${userData._id}/active`)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ width: 60, height: 60 }} src={userData.profileImageUrl || undefined} alt={userData.displayName}>
              {!userData.profileImageUrl && getInitials(userData.displayName)}
            </Avatar>
            <Box sx={{ ml: 2 }}>
              <Typography variant="h6">{userData.displayName}</Typography>
              <Typography variant="body2" color="text.secondary">
                Trust Score: {(userData.trustScore || 50).toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Member since {formattedDate}
              </Typography>
            </Box>
          </Box>
          {isMobile && (
            <IconButton onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }} sx={{ ml: 1 }}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      </Paper>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
          <Typography variant="h6" gutterBottom>Activity Overview</Typography>
          {[
            { icon: StarIcon,           value: userData.stats?.reviews?.totalReviews || 0,        label: 'Reviews Written'        },
            { icon: FollowTheSignsIcon, value: userData.stats?.follows?.totalFollows || 0,         label: 'Businesses Following'   },
            { icon: HowToRegIcon,       value: userData.stats?.referrals?.totalReferrals || 0,     label: 'Businesses Referred'    },
            { icon: VisibilityIcon,     value: userData.stats?.pageViews?.totalPageViews || 0,     label: 'Businesses Viewed'      },
          ].map(({ icon: Icon, value, label }) => (
            <Box key={label} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
              <Icon sx={{ color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" color="primary.main">{value}</Typography>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
              </Box>
            </Box>
          ))}
        </Paper>
      </Collapse>
    </Box>
  );
};

// ===== QuickActions — unchanged =====
const QuickActions = ({ isLoading }) => {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3, border: 'none' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2 }}>
          <Skeleton variant="rectangular" height={isMobile ? 56 : 40} />
          <Skeleton variant="rectangular" height={isMobile ? 56 : 40} />
        </Box>
      </Paper>
    );
  }

  if (isMobile) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3, border: 'none' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Button variant="contained" startIcon={<SearchIcon />} onClick={() => navigate('/find')} fullWidth sx={{ py: 1.5 }}>
            Find
          </Button>
          <Button variant="contained" startIcon={<AddBusinessIcon />} onClick={() => navigate('/company/new')} fullWidth sx={{ py: 1.5 }}>
            Add
          </Button>
        </Box>
        <Button variant="contained" color="warning" startIcon={<ReviewIcon />} endIcon={<WarningIcon />} fullWidth sx={{ py: 1.5 }}>
          Request Reviews
        </Button>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, border: 'none' }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<SearchIcon />} onClick={() => navigate('/find')} size="small">
          Find Business
        </Button>
        <Button variant="contained" startIcon={<AddBusinessIcon />} onClick={() => navigate('/company/new')} size="small">
          Add Business
        </Button>
        <Button variant="contained" color="warning" startIcon={<ReviewIcon />} endIcon={<WarningIcon />} size="small">
          Request Reviews
        </Button>
      </Box>
    </Paper>
  );
};

// ===== ForYouSection — unchanged =====
const ForYouSection = ({ recommendations, isLoading }) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3, border: 'none' }}>
        <Skeleton variant="text" width={200} height={32} />
        <Box sx={{ mt: 2 }}>
          {[1, 2].map((i) => <Skeleton key={i} variant="rectangular" height={120} sx={{ mb: 2 }} />)}
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, border: 'none' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>For You</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {recommendations.slice(0, 2).map((business) => (
          <Paper
            key={business._id}
            elevation={0}
            sx={{ p: 2, bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { bgcolor: 'grey.100' } }}
            onClick={() => navigate(`/business/${business._id}/${business.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle1" gutterBottom>{business.businessName}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>{business.category[0]}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Rating value={business.avgSkillRating_all} readOnly size="small" />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    Smart Rating: {Math.floor(business.SkillValue_all * 10) / 10} ★
                  </Typography>
                </Box>
              </Box>
              <BusinessInsightChip type={business.insightType} value={business.insightValue} />
            </Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
};

// ===== NetworkActivity — unchanged =====
const NetworkActivity = ({ activities, isLoading }) => {
  const navigate = useNavigate();

  const getActivityContent = (activity) => {
    let firstLine, secondLine;
    switch (activity.action) {
      case 'followedBusiness': firstLine = `now follows ${activity.businessName}`;      secondLine = activity.details || '';  break;
      case 'BusinessViewed':   firstLine = `viewed ${activity.businessName}`;           secondLine = activity.details || '';  break;
      case 'reviewed':         firstLine = `reviewed ${activity.businessName}`;         secondLine = activity.summary || '';  break;
      case 'endorsed':         firstLine = `referred ${activity.businessName}`;         secondLine = '';                      break;
      case 'likedReview':      firstLine = `${activity.likeAction} a review of ${activity.businessName}`; secondLine = activity.summary || ''; break;
      default:                 firstLine = `performed an action on ${activity.businessName}`;             secondLine = activity.details || ''; break;
    }
    return { firstLine, secondLine, thirdLine: moment(activity.creationDate).fromNow() };
  };

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
        <Skeleton variant="text" width={200} height={32} />
        {[1, 2, 3].map((i) => <Box key={i} sx={{ mt: 2 }}><Skeleton variant="rectangular" height={80} /></Box>)}
      </Paper>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
        <Typography variant="h6" gutterBottom>Network Activity</Typography>
        <Alert severity="info" sx={{ mt: 2 }}>No reviews or references yet. This member is just getting started!</Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
      <Typography variant="h6" gutterBottom>Network Activity</Typography>
      {activities.slice(0, 4).map((activity) => {
        const { firstLine, secondLine, thirdLine } = getActivityContent(activity);
        return (
          <Paper key={activity.id} elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar alt={activity.userDisplayName} src={activity.userPhoto} sx={{ mr: 2, cursor: 'pointer' }}
                onClick={() => navigate(`/profile/${activity.userId}/active`)} />
              <Box sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/business/${activity.businessId}/${activity.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`)}>
                <Typography variant="body1">{activity.userDisplayName} {firstLine}</Typography>
                {secondLine && <Typography variant="body2" color="text.secondary">{secondLine}</Typography>}
                <Typography variant="caption" color="text.secondary">{thirdLine}</Typography>
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Paper>
  );
};

// ===== InsightsAndTrending — unchanged =====
const NetworkInsight = ({ icon: Icon, title, value }) => (
  <Box sx={{ p: 2, bgcolor: 'grey.50', mb: 2, borderRadius: 1, display: 'flex', alignItems: 'center' }}>
    <Icon sx={{ color: 'primary.main', mr: 2 }} />
    <Box>
      <Typography variant="h6" color="primary.main">{value}</Typography>
      <Typography variant="body2" color="text.secondary">{title}</Typography>
    </Box>
  </Box>
);

const InsightsAndTrending = ({ trendingBusinesses, isLoading, networkStats, netSize }) => {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('md'));
  const [networkExpanded,  setNetworkExpanded]  = useState(!isMobile);
  const [trendingExpanded, setTrendingExpanded] = useState(!isMobile);

  useEffect(() => { setNetworkExpanded(!isMobile); setTrendingExpanded(!isMobile); }, [isMobile]);

  const networkInsights = [
    { icon: HowToRegIcon,      title: 'Total businesses referred by your network', value: networkStats?.networkReferred || 0 },
    { icon: StarIcon,          title: 'Unique businesses reviewed by network',      value: networkStats?.networkReviews  || 0 },
    { icon: PeopleIcon,        title: 'People in your extended network',            value: netSize || 0                      },
    { icon: AddBusinessIcon,   title: 'Businesses followed by your network',        value: networkStats?.networkFollows  || 0 },
  ];

  if (isLoading) {
    return (
      <Box>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, border: 'none' }}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 2 }} />)}
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, border: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: networkExpanded ? 2 : 0 }}>
          <Typography variant="h6">Network Impact</Typography>
          {isMobile && (
            <IconButton onClick={() => setNetworkExpanded(!networkExpanded)} size="small">
              {networkExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
        <Collapse in={networkExpanded} timeout="auto" unmountOnExit>
          {networkInsights.map((insight, i) => (
            <NetworkInsight key={i} icon={insight.icon} title={insight.title} value={insight.value} />
          ))}
        </Collapse>
      </Paper>

      {trendingBusinesses?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: trendingExpanded ? 2 : 0 }}>
            <Typography variant="h6">Trending Near You</Typography>
            {isMobile && (
              <IconButton onClick={() => setTrendingExpanded(!trendingExpanded)} size="small">
                {trendingExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
          <Collapse in={trendingExpanded} timeout="auto" unmountOnExit>
            {trendingBusinesses.map((business) => (
              <Paper
                key={business._id}
                elevation={0}
                sx={{ p: 2, mb: 2, bgcolor: 'grey.50', cursor: 'pointer' }}
                onClick={() => navigate(`/business/${business._id}/${business.businessName.replace(/[\s,\.]+/g, '-').toLowerCase()}`)}
              >
                <Typography variant="subtitle2">{business.businessName}</Typography>
                <Typography variant="body2" color="text.secondary">{business.category[0]}</Typography>
              </Paper>
            ))}
          </Collapse>
        </Paper>
      )}
    </Box>
  );
};

// ===== Platform guide — unchanged =====
const PlatformGuide = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ mt: 3, p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
      <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
        Welcome to Your Network Platform
      </Typography>
      <Typography variant="body1" paragraph>
        Looks like your feed is waiting to be filled. Here are some actions to get started:
      </Typography>
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <GroupAdd color="primary" sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant="h6">Build Your Network</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Connect with professionals and businesses to expand your opportunities.
              </Typography>
            </CardContent>
            <CardActions>
              <Button size="small" color="primary" onClick={() => navigate('/usersearch')}>Find Connections</Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessCenterIcon color="primary" sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant="h6">Explore Businesses</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Discover trending businesses and get personalized recommendations.
              </Typography>
            </CardContent>
            <CardActions>
              <Button size="small" color="primary" onClick={() => navigate('/find')}>Explore Now</Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// ===== VIEW TOGGLE =====
const ViewToggle = ({ view, onChange }) => (
  <Tabs
    value={view}
    onChange={(_, v) => onChange(v)}
    sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
  >
    <Tab value="discovery" label="Discover"       icon={<SearchIcon   fontSize="small" />} iconPosition="start" />
    <Tab value="business"  label="Business Owner" icon={<BusinessIcon fontSize="small" />} iconPosition="start" />
  </Tabs>
);

// ===== BUSINESS DASHBOARD COMPONENTS =====

// Business selector card (left column)
const BusinessCard = ({ business, isSelected, onSelect }) => (
  <Paper
    elevation={0}
    onClick={() => onSelect(business._id)}
    sx={{
      p:           1.5,
      mb:          1.5,
      cursor:      'pointer',
      border:      '2px solid',
      borderColor: isSelected ? 'primary.main' : 'divider',
      borderRadius: 2,
      bgcolor:     isSelected ? 'primary.50' : 'background.paper',
      transition:  'all 0.2s ease',
      '&:hover':   { borderColor: 'primary.main', bgcolor: 'primary.50' }
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Avatar src={business.photoUrl || undefined} sx={{ width: 44, height: 44, borderRadius: 1 }} variant="rounded">
        {business.businessName?.[0]}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>{business.businessName}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {business.skillName || business.category?.[0] || 'Business'}
        </Typography>
        {business.avgSkillRating_all > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StarIcon sx={{ fontSize: 12, color: 'warning.main' }} />
            <Typography variant="caption" color="text.secondary">
              {business.avgSkillRating_all.toFixed(1)}
            </Typography>
          </Box>
        )}
      </Box>
      {isSelected && <CheckCircleIcon color="primary" fontSize="small" sx={{ flexShrink: 0 }} />}
    </Box>
  </Paper>
);

// Stats row — 4 cards
const StatCard = ({ icon: Icon, label, value, color = 'primary', loading }) => (
  <Paper
    elevation={0}
    sx={{ p: 2, bgcolor: `${color}.50`, borderRadius: 2, border: '1px solid', borderColor: `${color}.100`, textAlign: 'center' }}
  >
    {loading
      ? <Skeleton variant="rectangular" height={60} />
      : (
        <>
          <Icon sx={{ color: `${color}.main`, fontSize: 28, mb: 0.5 }} />
          <Typography variant="h5" fontWeight={700} color={`${color}.main`}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </>
      )
    }
  </Paper>
);

// Draft type chip
const DraftTypeChip = ({ draftType, platform }) => {
  const config = {
    social_post:     { label: platform || 'Social Post', color: 'primary',   icon: <SocialIcon sx={{ fontSize: 14 }} /> },
    review_response: { label: 'Review Response',          color: 'warning',   icon: <RateReviewIcon sx={{ fontSize: 14 }} /> },
    faq:             { label: 'FAQ',                      color: 'info',      icon: <FAQIcon sx={{ fontSize: 14 }} /> },
    blog_post:       { label: 'Blog Post',                color: 'secondary', icon: <ArticleIcon sx={{ fontSize: 14 }} /> },
    bio:             { label: 'Bio',                      color: 'success',   icon: <BusinessIcon sx={{ fontSize: 14 }} /> },
  };
  const c = config[draftType] || { label: draftType, color: 'default', icon: null };
  return (
    <Chip
      size="small"
      label={c.label}
      color={c.color}
      icon={c.icon}
      variant="outlined"
      sx={{ fontSize: '0.7rem' }}
    />
  );
};

// Single pending draft item with inline approve/reject
const PendingDraftItem = ({ draft, businessId, token }) => {
  const [approveDraft, { isLoading: approving }] = useApproveDraftMutation();
  const [rejectDraft,  { isLoading: rejecting  }] = useRejectDraftMutation();

  const handleApprove = async () => {
    try {
      await approveDraft({ draftId: draft._id, businessId }).unwrap();
    } catch (e) {
      console.error('Approve error:', e);
    }
  };

  const handleReject = async () => {
    try {
      await rejectDraft({ draftId: draft._id, businessId }).unwrap();
    } catch (e) {
      console.error('Reject error:', e);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
        <DraftTypeChip draftType={draft.draftType} platform={draft.platform} />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>
          {moment(draft.createdAt).fromNow()}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.primary" sx={{
        mb:             1,
        display:        '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow:        'hidden',
        lineHeight:      1.5
      }}>
        {draft.content}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={handleApprove}
          disabled={approving || rejecting}
          startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          sx={{ fontSize: '0.7rem', py: 0.5 }}
        >
          {approving ? 'Approving...' : 'Approve'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={handleReject}
          disabled={approving || rejecting}
          startIcon={<RejectIcon sx={{ fontSize: 14 }} />}
          sx={{ fontSize: '0.7rem', py: 0.5 }}
        >
          {rejecting ? 'Rejecting...' : 'Reject'}
        </Button>
      </Box>
    </Paper>
  );
};

// Pending drafts section
const PendingDraftsSection = ({ businessId, token, navigate, business }) => {
  const { data: draftsData, isLoading } = useGetDraftsQuery(
    { businessId, status: 'pending_approval' },
    { skip: !businessId }
  );

  const drafts  = draftsData?.drafts || [];
  const total   = draftsData?.total  || 0;
  const bizSlug = business?.businessName?.replace(/[\s,\.]+/g, '-').toLowerCase();

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, border: 'none', mb: 2 }}>
        <Skeleton variant="text" width="40%" height={32} sx={{ mb: 1 }} />
        {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={90} sx={{ mb: 1, borderRadius: 1 }} />)}
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, border: 'none', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PendingIcon color="warning" fontSize="small" />
          <Typography variant="h6">Pending Approval</Typography>
          {total > 0 && (
            <Chip label={total} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
          )}
        </Box>
        {total > 3 && (
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate(`/business/${businessId}/${bizSlug}`)}
          >
            View All
          </Button>
        )}
      </Box>

      {total === 0
        ? (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            All caught up — no drafts pending approval
          </Alert>
        ) : (
          <>
            {drafts.slice(0, 3).map(draft => (
              <PendingDraftItem
                key={draft._id}
                draft={draft}
                businessId={businessId}
                token={token}
              />
            ))}
            {total > 3 && (
              <Button
                fullWidth
                variant="outlined"
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(`/business/${businessId}/${bizSlug}`)}
                sx={{ mt: 1 }}
              >
                {total - 3} more drafts on business page
              </Button>
            )}
          </>
        )
      }
    </Paper>
  );
};

// Content summary (right column)
const ContentSummary = ({ businessId, bizStats, statsLoading }) => {
  const { data: publishedData, isLoading: publishedLoading } = useGetDraftsQuery(
    { businessId, status: 'published' },
    { skip: !businessId }
  );

  const published = publishedData?.drafts || [];

  const counts = {
    social_post:     published.filter(d => d.draftType === 'social_post').length,
    review_response: published.filter(d => d.draftType === 'review_response').length,
    faq:             published.filter(d => d.draftType === 'faq').length,
    blog_post:       published.filter(d => d.draftType === 'blog_post').length,
  };

  const lastContentDate = bizStats ? null : null; // comes from business card

  const loading = publishedLoading || statsLoading;

  const summaryItems = [
    { icon: SocialIcon,       label: 'Social Posts Published',    value: counts.social_post,     color: 'primary'   },
    { icon: RateReviewIcon,   label: 'Review Responses Posted',   value: counts.review_response, color: 'warning'   },
    { icon: FAQIcon,          label: 'FAQs Published',            value: counts.faq,             color: 'info'      },
    { icon: ArticleIcon,      label: 'Blog Posts Published',      value: counts.blog_post,       color: 'secondary' },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AIIcon color="primary" fontSize="small" />
        <Typography variant="h6">Content Summary</Typography>
      </Box>

      {loading
        ? [1, 2, 3, 4].map(i => <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />)
        : summaryItems.map(({ icon: Icon, label, value, color }) => (
          <Box
            key={label}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1, bgcolor: 'grey.50', borderRadius: 1 }}
          >
            <Icon sx={{ color: `${color}.main`, fontSize: 20, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
            <Typography variant="body2" fontWeight={700} color={`${color}.main`}>{value}</Typography>
          </Box>
        ))
      }

      {bizStats && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: 'primary.50', borderRadius: 1 }}>
            <VisibilityIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="body2" sx={{ flex: 1 }}>Total Page Views</Typography>
            <Typography variant="body2" fontWeight={700} color="primary.main">
              {bizStats?.stats?.pageViews?.totalPageViews || 0}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mt: 1, bgcolor: 'success.50', borderRadius: 1 }}>
            <StarIcon sx={{ color: 'success.main', fontSize: 20 }} />
            <Typography variant="body2" sx={{ flex: 1 }}>Reviews Received</Typography>
            <Typography variant="body2" fontWeight={700} color="success.main">
              {bizStats?.stats?.reviews?.totalReviews || 0}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

// Business quick actions (right column)
const BusinessQuickActions = ({ business, navigate }) => {
  const bizSlug = business?.businessName?.replace(/[\s,\.]+/g, '-').toLowerCase();
  const actions = [
    {
      icon:   ArrowForwardIcon,
      label:  'Go to Business Page',
      color:  'primary',
      action: () => navigate(`/business/${business._id}/${bizSlug}`)
    },
    {
      icon:   AddBusinessIcon,
      label:  'Add Another Business',
      color:  'primary',
      action: () => navigate('/company/new')
    },
    {
      icon:   SearchIcon,
      label:  'Explore Platform',
      color:  'primary',
      action: () => navigate('/find')
    },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, border: 'none', mb: 2 }}>
      <Typography variant="h6" gutterBottom>Quick Actions</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {actions.map(({ icon: Icon, label, color, action }, i) => (
          <Button
            key={i}
            fullWidth
            variant={i === 0 ? 'contained' : 'outlined'}
            color={color}
            startIcon={<Icon />}
            onClick={action}
            sx={{ justifyContent: 'flex-start', py: 1 }}
          >
            {label}
          </Button>
        ))}
      </Box>
    </Paper>
  );
};

// Selected business header
const BusinessHeader = ({ business, navigate, bizStats, statsLoading }) => {
  const bizSlug = business?.businessName?.replace(/[\s,\.]+/g, '-').toLowerCase();

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, bgcolor: 'primary.50' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          src={business.photoUrl || undefined}
          sx={{ width: 56, height: 56, borderRadius: 2 }}
          variant="rounded"
        >
          {business.businessName?.[0]}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap>{business.businessName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {business.skillName || business.category?.[0] || 'Business'}
          </Typography>
          {business.avgSkillRating_all > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Rating value={business.avgSkillRating_all} readOnly size="small" precision={0.5} />
              <Typography variant="caption" color="text.secondary">
                ({business.avgSkillRating_all.toFixed(1)})
              </Typography>
            </Box>
          )}
        </Box>
        <Tooltip title="Go to business page">
          <IconButton
            color="primary"
            onClick={() => navigate(`/business/${business._id}/${bizSlug}`)}
          >
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

// SelectedBusinessView — manages all queries for selected business
const SelectedBusinessView = ({ business, token, navigate, isMobile }) => {
  const businessId = business._id;

  const {
    data:      bizStats,
    isLoading: statsLoading
  } = useGetBusinessStatsQuery(
    { businessId, authType: 'token', token },
    { skip: !businessId || !token }
  );

  const statsRow = [
    {
      icon:    PendingIcon,
      label:   'Pending Approval',
      value:   '—',
      color:   'warning'
    },
    {
      icon:    StarIcon,
      label:   'Reviews Received',
      value:   statsLoading ? '...' : (bizStats?.stats?.reviews?.totalReviews || 0),
      color:   'success'
    },
    {
      icon:    VisibilityIcon,
      label:   'Page Views',
      value:   statsLoading ? '...' : (bizStats?.stats?.pageViews?.totalPageViews || 0),
      color:   'info'
    },
    {
      icon:    ScheduleIcon,
      label:   'Last Content',
      value:   business.lastContentGeneratedAt
        ? moment(business.lastContentGeneratedAt).fromNow()
        : 'Never',
      color:   'primary'
    },
  ];

  // Get pending count separately to update the stats row
  const { data: pendingData } = useGetDraftsQuery(
    { businessId, status: 'pending_approval' },
    { skip: !businessId }
  );
  statsRow[0].value = pendingData?.total ?? '—';

  if (isMobile) {
    return (
      <Box>
        <BusinessHeader business={business} navigate={navigate} bizStats={bizStats} statsLoading={statsLoading} />

        {/* Stats 2x2 grid */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {statsRow.map((stat) => (
            <Grid key={stat.label} item xs={6}>
              <StatCard
                icon={stat.icon}
                label={stat.label}
                value={stat.value}
                color={stat.color}
                loading={statsLoading}
              />
            </Grid>
          ))}
        </Grid>

        <PendingDraftsSection
          businessId={businessId}
          token={token}
          navigate={navigate}
          business={business}
        />

        <ContentSummary businessId={businessId} bizStats={bizStats} statsLoading={statsLoading} />

        <BusinessQuickActions business={business} navigate={navigate} />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Middle column */}
      <Grid item xs={12} md={8}>
        <BusinessHeader business={business} navigate={navigate} bizStats={bizStats} statsLoading={statsLoading} />

        {/* Stats row */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {statsRow.map((stat) => (
            <Grid key={stat.label} item xs={6} sm={3}>
              <StatCard
                icon={stat.icon}
                label={stat.label}
                value={stat.value}
                color={stat.color}
                loading={statsLoading}
              />
            </Grid>
          ))}
        </Grid>

        <PendingDraftsSection
          businessId={businessId}
          token={token}
          navigate={navigate}
          business={business}
        />
      </Grid>

      {/* Right column */}
      <Grid item xs={12} md={4}>
        <BusinessQuickActions business={business} navigate={navigate} />
        <ContentSummary businessId={businessId} bizStats={bizStats} statsLoading={statsLoading} />
      </Grid>
    </Grid>
  );
};

// Full business dashboard tab
const BusinessDashboard = ({ user, token, isMobile }) => {
  const navigate   = useNavigate();
  const businesses = user?.myBusinesses || [];

  const [selectedBizId, setSelectedBizId] = useState(
    businesses.length > 0 ? businesses[0]._id : null
  );

  const selectedBusiness = businesses.find(b => b._id === selectedBizId);

  if (businesses.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <AIIcon sx={{ fontSize: 64, color: 'primary.light', mb: 2 }} />
        <Typography variant="h5" gutterBottom>No businesses yet</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Add your business to unlock AI content generation and review management
        </Typography>
        <Button variant="contained" size="large" startIcon={<AddBusinessIcon />}
          onClick={() => navigate('/company/new')}>
          Add Your Business
        </Button>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box>
        {/* Horizontal business chip selector for mobile */}
        {businesses.length > 1 && (
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, mb: 2, '&::-webkit-scrollbar': { display: 'none' } }}>
            {businesses.map(biz => (
              <Chip
                key={biz._id}
                label={biz.businessName}
                avatar={<Avatar src={biz.photoUrl} sx={{ width: 24, height: 24 }}>{biz.businessName?.[0]}</Avatar>}
                onClick={() => setSelectedBizId(biz._id)}
                color={selectedBizId === biz._id ? 'primary' : 'default'}
                variant={selectedBizId === biz._id ? 'filled' : 'outlined'}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
        )}

        {selectedBusiness && (
          <SelectedBusinessView
            business={selectedBusiness}
            token={token}
            navigate={navigate}
            isMobile={true}
          />
        )}
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Left column — business selector */}
      <Grid item xs={12} md={3}>
        <Paper variant="outlined" sx={{ p: 2, border: 'none' }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" fontSize="small" />
            My Businesses ({businesses.length})
          </Typography>

          {businesses.map(biz => (
            <BusinessCard
              key={biz._id}
              business={biz}
              isSelected={selectedBizId === biz._id}
              onSelect={setSelectedBizId}
            />
          ))}

          <Divider sx={{ my: 2 }} />

          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddBusinessIcon />}
            onClick={() => navigate('/company/new')}
            size="small"
          >
            Add Another Business
          </Button>
        </Paper>
      </Grid>

      {/* Right section — selected business content */}
      <Grid item xs={12} md={9}>
        {selectedBusiness
          ? (
            <SelectedBusinessView
              business={selectedBusiness}
              token={token}
              navigate={navigate}
              isMobile={false}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                Select a business to view its dashboard
              </Typography>
            </Box>
          )
        }
      </Grid>
    </Grid>
  );
};

// ===== MAIN HOMEPAGE COMPONENT =====
const HomePage = () => {
  const navigate   = useNavigate();
  const dispatch   = useDispatch();
  const location   = useLocation();
  const theme      = useTheme();
  const isMobile   = useMediaQuery(theme.breakpoints.down('md'));

  const prefetchGetStatsGlobal  = useGetStatsGlobalPrefetch('getStatsGlobal');
  const prefetchGetUserChatList = useGetUserChatListPrefetch('getUserChatList');
  const prefetchGetNotifications = useGetUserNotificationsPrefetch('getUserNotifications');
  const [setAffiliateAttribution] = useSetAffiliateAttributionMutation();

  const [isLoading,                setIsLoading]                = useState(true);
  const [email,                    setEmail]                    = useState(null);
  const [uId,                      setUId]                      = useState(null);
  const [dataLoaded,               setDataLoaded]               = useState(false);
  const [showRedirectNotification, setShowRedirectNotification] = useState(false);
  const [redirectLink,             setRedirectLink]             = useState(null);

  const token  = useSelector((state) => state.auth?.token || null);
  const userId = useSelector((state) => state.auth?.userId || '');
  const user   = useSelector((state) => state.auth?.user || '');

  const { data: userProfileData } = useGetUserProfileQuery(userId, { skip: !userId });
  const hasBusinesses = userProfileData?.myBusinesses?.length > 0;

  const { data: userData, isLoading: isUserLoading } = useGetUserByEmailQuery(email, {
    skip: !email,
    refetchOnMountOrArgChange: true
  });

  const activityArgs = { userId: uId, activityType: 'providers' };
  const { data: netActivity,    isLoading: isActivityLoading     } = useGetActivitiesQuery(activityArgs,    { skip: !uId });
  const { data: trendingData,   isLoading: isTrendingLoading     } = useGetUserTrendingQuery(activityArgs,  { skip: !uId });
  const { data: networkStatsData, isLoading: isNetworkStatsDataLoading } = useGetNetworkStatsQuery({ skip: !uId });

  
  const [view, setView] = useState(null);

  // users.goals (onboarding intent) — a business-intent owner with no business yet
  // should land on the Business tab, whose empty state is "Add Your Business".
  const wantsBusiness = Array.isArray(userData?.goals) && userData.goals.includes('business');

  useEffect(() => {
    if (userProfileData) {
      setView(userProfileData.myBusinesses?.length > 0 || wantsBusiness ? 'business' : 'discovery');
    }
  }, [userProfileData, wantsBusiness]);

  // Auto-switch to business if businesses detected after load
  useEffect(() => {
    if (user?.myBusinesses?.length > 0 && view !== 'business') {
      setView('business');
    }
  }, [user?.myBusinesses]);

  // SEO
  const seoData = (() => {
    if (isLoading || !user) return {
      title:       'Loading Dashboard - Revfer',
      description: 'Loading your personalized dashboard.'
    };
    const userName = user.displayName || 'User';
    return {
      title:       `${userName}'s Dashboard - Revfer`,
      description: view === 'business'
        ? `Manage your businesses on Revfer. View pending drafts, review stats and publish AI-generated content.`
        : `Welcome back ${userName}! Discover trusted businesses, manage your network and stay connected.`
    };
  })();

  // Redirect link
  useEffect(() => {
    if (location.state?.link) {
      setRedirectLink(location.state.link);
      setShowRedirectNotification(true);
    }
  }, [location.state]);

  // Fetch email from session
  useEffect(() => {
    const getEmail = async () => {
      try {
        const session = await fetchAuthSession();
        setEmail(session.tokens.idToken.payload.email);
      } catch (e) {
        console.log(e);
      }
    };
    getEmail();
  }, []);

  // Loading state
  useEffect(() => {
    setIsLoading(isUserLoading || !userData || isActivityLoading || isTrendingLoading || isNetworkStatsDataLoading);
  }, [isUserLoading, email, userData, isActivityLoading, isTrendingLoading, isNetworkStatsDataLoading]);

  // Setup user + prefetch
  useEffect(() => {
    if (userData) {
      dispatch(setSignIn(userData));
      setUId(userData._id);
      const prefetchAll = async () => {
        await Promise.all([
          prefetchGetStatsGlobal(),
          prefetchGetUserChatList(userData._id),
          prefetchGetNotifications(),
        ]);
        setDataLoaded(true);
      };
      prefetchAll();
    }
  }, [userData, navigate, dispatch, prefetchGetStatsGlobal, prefetchGetUserChatList, prefetchGetNotifications]);

  // Affiliate attribution promotion — runs once after user is loaded.
  // Reads localStorage, calls backend, clears on success.
  useEffect(() => {
    if (!dataLoaded || !userData?._id) return;

    const raw = localStorage.getItem('revfer_ref');
    if (!raw) return;

    let attribution;
    try {
      attribution = JSON.parse(raw);
    } catch {
      // Malformed JSON — discard and stop
      localStorage.removeItem('revfer_ref');
      return;
    }

    if (!attribution?.code || !attribution?.captured_at) {
      localStorage.removeItem('revfer_ref');
      return;
    }

    // Client-side expiry check (server validates too)
    if (attribution.expires_at) {
      try {
        if (new Date(attribution.expires_at).getTime() < Date.now()) {
          localStorage.removeItem('revfer_ref');
          return;
        }
      } catch {
        // Ignore parse error, let server decide
      }
    }

    setAffiliateAttribution({
      code: attribution.code,
      captured_at: attribution.captured_at
    })
      .unwrap()
      .then(() => {
        // Server has it now — clear browser-side storage
        localStorage.removeItem('revfer_ref');
        document.cookie = 'revfer_ref=; Max-Age=0; Path=/; SameSite=Lax; Secure';
      })
      .catch((err) => {
        // Leave localStorage in place — retry on next HomePage mount
        console.warn('Affiliate attribution call failed, will retry', err);
      });
  }, [dataLoaded, userData?._id, setAffiliateAttribution]);

  // Redirect NEW/EARLY users
  useEffect(() => {
    if (!userData || !dataLoaded) return;

    if (userData.userDisplayStatus === 'NEW' && location.state?.onboarding) {
      clearPendingDestination();
      navigate('/onboarding', { state: { user: userData, link: redirectLink } });
      return;
    }

    if ((userData.userDisplayStatus === 'EARLY' || !location.state?.onboarding)
        && userData.userDisplayStatus !== 'ACTIVE') {
      clearPendingDestination();
      navigate('/earlyuserdashboard', { state: { user: userData, link: redirectLink } });
      return;
    }

    // ACTIVE — honour the destination RouteGuard preserved.
    const dest = takePendingDestination();
    if (dest) {
      navigate(dest, { replace: true });
    }
  }, [userData, dataLoaded, navigate, redirectLink, location.state]);

  const hasRecommendations = userData?.recomendedBusinesses?.length > 0;
  const hasNetworkActivity = netActivity?.length > 0;

  if (isLoading  || !view) {
    return (
      <>
        <BasicSEO title="Loading Dashboard - Revfer" description="Loading your dashboard." noIndex={true} />
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}><Skeleton variant="rectangular" height={300} /></Grid>
            <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={400} /></Grid>
            <Grid item xs={12} md={3}><Skeleton variant="rectangular" height={300} /></Grid>
          </Grid>
        </Container>
      </>
    );
  }

  return (
    <>
      <BasicSEO title={seoData.title} description={seoData.description} noIndex={true} />

      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* Redirect notification */}
        <Snackbar
          open={showRedirectNotification && dataLoaded}
          autoHideDuration={15000}
          onClose={() => setShowRedirectNotification(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ marginTop: 2 }}
        >
          <Alert
            severity="info"
            action={
              <>
                <Button color="inherit" size="small" onClick={() => { setShowRedirectNotification(false); if (redirectLink) navigate(redirectLink); }}>
                  Go back to page
                </Button>
                <IconButton size="small" color="inherit" onClick={() => setShowRedirectNotification(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            Would you like to return to the page you were viewing?
          </Alert>
        </Snackbar>

        {/* View toggle — only show if user has businesses */}
        {(hasBusinesses || wantsBusiness) && view && (
          <ViewToggle view={view} onChange={setView} />
        )}
        {/* ===== BUSINESS VIEW ===== */}
        {view === 'business' && (
          <BusinessDashboard
            user={userProfileData}
            token={token}
            isMobile={isMobile}
          />
        )}

        {/* ===== DISCOVERY VIEW — existing layout unchanged ===== */}
        {view === 'discovery' && (
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={3}>
              <ActivityOverview userData={user} isLoading={isLoading} />
            </Grid>

            {/* Middle Column */}
            <Grid item xs={12} md={6}>
              <QuickActions isLoading={isLoading} />
              {isLoading
                ? (
                  <>
                    <Box sx={{ mt: 3 }}><Skeleton variant="rectangular" width="100%" height={200} /></Box>
                    <Box sx={{ mt: 3 }}><Skeleton variant="rectangular" width="100%" height={300} /></Box>
                  </>
                )
                : !hasRecommendations && !hasNetworkActivity
                  ? <PlatformGuide />
                  : (
                    <>
                      {hasRecommendations && (
                        <ForYouSection recommendations={userData?.recomendedBusinesses || []} isLoading={isLoading} />
                      )}
                      {hasNetworkActivity && (
                        <NetworkActivity activities={netActivity} isLoading={isActivityLoading || isLoading} />
                      )}
                    </>
                  )
              }
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={3}>
              <InsightsAndTrending
                trendingBusinesses={trendingData || []}
                isLoading={isTrendingLoading || isLoading || isNetworkStatsDataLoading}
                networkStats={networkStatsData}
                netSize={userData?.myCurrentNetworkList?.userDetails?.length}
              />
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
};

// Fix missing GroupAdd import
const GroupAdd = GroupAddIcon;

export default HomePage;